import type { SessionInfo, Match, Prompt, Team, Club, PlayerNote, MatchHighlight } from '../types';
import { locale } from 'svelte-i18n';
import { get } from 'svelte/store';
import { settingsStore } from '../stores/settings';

// Helper function to get current locale
function getCurrentLocale(): string {
  // First try to get from settingsStore (primary source)
  const settings = get(settingsStore);
  if (settings && settings.language) {
    return settings.language;
  }
  
  // Fallback to localStorage
  const storedLocale = localStorage.getItem('preferred-language');
  if (storedLocale) {
    return storedLocale;
  }
  
  // Then try to get from svelte-i18n store
  const currentLocale = get(locale);
  if (currentLocale) {
    // Store it in localStorage for future use
    localStorage.setItem('preferred-language', currentLocale);
    return currentLocale;
  }
  
  // Default to Dutch if nothing is found
  const defaultLocale = 'nl';
  localStorage.setItem('preferred-language', defaultLocale);
  return defaultLocale;
}

// Helper function to get current token dynamically
function getCurrentToken(): string {
  return localStorage.getItem('token') || '';
}

// Base API request with language header (without Authorization - will be added dynamically)
const api_request = {
  method: 'GET',
  mode: 'same-origin',
  cache: 'no-cache',
  credentials: 'same-origin',
  headers: {
    'Content-Type': 'application/json',
    'Accept-Language': getCurrentLocale(),
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    'Expires': '0'
  },
  redirect: 'follow',
  referrerPolicy: 'origin',
};

const get_request = { 
  ...api_request, 
  method: 'GET',
};
const post_request = { ...api_request, method: 'POST' };
const put_request = { ...api_request, method: 'PUT' };
const delete_request = { ...api_request, method: 'DELETE' };
const formdata_request = {
  ...api_request,
  method: 'POST',
  headers: {
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    'Accept-Language': getCurrentLocale()
  },
};

// Constants and helper functions
// Use type assertion for environment variables
const API_URL = (import.meta as any).env?.VITE_API_URL || '/api';

function replacer(key: string, value: any) {
  if (value === undefined) {
    return null;
  }
  return value;
}

// Main caller function
async function doAPICall(
  url: string, 
  data: any, 
  callback: (result: any, status: number) => any,
  customHeaders?: Record<string, string>
) {
  try {
    // Check if we need to update the language header with latest locale
    const currentLocale = getCurrentLocale();
    let headers = { ...data.headers };
    
    // Only update Accept-Language if it's different from what's in headers
    if (headers['Accept-Language'] !== currentLocale) {
      headers['Accept-Language'] = currentLocale;
    }
    
    // Add Authorization header dynamically (unless it's already present)
    if (!headers['Authorization']) {
      const currentToken = getCurrentToken();
      if (currentToken) {
        headers['Authorization'] = `Bearer ${currentToken}`;
      }
    }
    
    // Add any custom headers
    if (customHeaders) {
      headers = { ...headers, ...customHeaders };
    }

    // Add cache-busting parameter for GET requests
    let finalUrl = url;
    if (data.method === 'GET') {
      const separator = url.includes('?') ? '&' : '?';
      finalUrl = `${url}${separator}_t=${Date.now()}`;
    }

    const requestData = {
      ...data,
      headers
    };

    const response = await fetch(finalUrl, requestData);
    const result = await response.json();

    if (!response.ok) {
      // // Handle token expiration
      // if (response.status === 401) {
      //   // Clear the token
      //   localStorage.removeItem('token');
      //   // Only redirect if we're not already on the login page
      //   if (!window.location.pathname.includes('/login')) {
      //     window.location.href = '/login';
      //   }
      //   return;
      // }

      console.log('response is not ok', result);
      if (result.error) {
        result.errors = [result.error];
      }
      if (result.errors) {
        let errorMessage = result.errors.join(' ');
        return callback(errorMessage, response.status);
      }
      return callback('Something went wrong, please try again', response.status);
    }
    return callback(result, response.status);
  } catch (error) {
    console.error('API call error:', error);
    return callback('Something went wrong, please try again', 400);
  }
}

// Auth-related API calls
export async function requestMagicLink(email: string, callback: (result: { token: string }, status: number) => void) {
  const data = { 
    ...post_request, 
    body: JSON.stringify({ email }) 
  };
  return doAPICall(`${API_URL}/auth/magic-link`, data, callback);
}

export async function verifyMagicLink(token: string, callback: (result: { access_token: string }, status: number) => void) {
  const data = { 
    ...post_request, 
    body: JSON.stringify({ token }) 
  };
  return doAPICall(`${API_URL}/auth/verify`, data, callback);
}

export async function getCurrentUser(callback: (result: any, status: number) => void) {
  const data = { 
    ...get_request
    // Authorization header will be added dynamically in doAPICall
  };
  return doAPICall(`${API_URL}/auth/me`, data, callback);
}

// API Functions

function getRequestWithAuth(baseRequest: typeof api_request) {
  return {
    ...baseRequest
    // Authorization header will be added dynamically in doAPICall
    // Don't add Accept-Language here since it's already in baseRequest.headers
  };
}

export async function getSessionInfo(callback: (result: SessionInfo, status: number) => void) {
  const data = getRequestWithAuth(get_request);
  return doAPICall(`${API_URL}/session/`, data, callback);
}

export async function updateTeam(teamData: Team, callback: (result: Team, status: number) => void) {
  const data = { ...post_request, body: JSON.stringify(teamData, replacer) };
  return doAPICall(`${API_URL}/session/team`, data, callback);
}

export async function saveMatch(reportData: Partial<Match>, callback: (result: Match, status: number) => void) {
  const data = {
    ...getRequestWithAuth(post_request),
    body: JSON.stringify(reportData, replacer)
  };
  return doAPICall(`${API_URL}/reports/`, data, callback);
}

export async function getMatches(callback: (result: Match[], status: number) => void) {
  const data = getRequestWithAuth(get_request);
  return doAPICall(`${API_URL}/reports/`, data, callback);
}

export async function getReport(id: string, callback: (result: Match, status: number) => void) {
  const data = getRequestWithAuth(get_request);
  return doAPICall(`${API_URL}/reports/${id}`, data, callback);
}

export async function updateMatch(id: string, reportData: Partial<Match>, callback: (result: Match, status: number) => void) {
  const data = {
    ...getRequestWithAuth(put_request),
    body: JSON.stringify(reportData, replacer)
  };
  return doAPICall(`${API_URL}/reports/${id}`, data, callback);
}

export async function deleteMatch(id: string, callback: (result: void, status: number) => void) {
  const data = getRequestWithAuth(delete_request);
  return doAPICall(`${API_URL}/reports/${id}`, data, callback);
}

export async function uploadMatchPhoto(reportId: string, type: string, file: File, callback: (result: { success: boolean, filename: string, url: string }, status: number) => void) {
  const formData = new FormData();
  formData.append('file', file);
  const data = {
    ...getRequestWithAuth(api_request),
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'Accept-Language': getCurrentLocale(),
      'X-Requested-With': 'XMLHttpRequest'
      // Authorization header will be added dynamically in doAPICall
    },
    body: formData
  };
  return doAPICall(`${API_URL}/reports/${reportId}/upload-photo/${type}`, data, callback);
}

export async function getUserPhotos(callback: (result: { photos: Array<{ filename: string, url: string, key_path: string }> }, status: number) => void) {
  const data = getRequestWithAuth(get_request);
  return doAPICall(`${API_URL}/reports/user-photos`, data, callback);
}

export async function deleteUserPhoto(filename: string, callback: (result: any, status: number) => void) {
  const data = {
    ...getRequestWithAuth(delete_request),
    body: JSON.stringify({ filename }, replacer)
  };
  return doAPICall(`${API_URL}/reports/user-photos/delete`, data, callback);
}

export async function trackMatchShare(matchId: string, shareType: 'announcement' | 'report', callback: (result: { success: boolean, share_type: string, timestamp: string }, status: number) => void) {
  const data = {
    ...getRequestWithAuth(post_request),
    body: JSON.stringify({ share_type: shareType }, replacer)
  };
  return doAPICall(`${API_URL}/reports/${matchId}/track-share`, data, callback);
}

export async function generateReport(params: {match_id: string}, callback: (result: any, status: number) => void) {
  const data = {
    ...getRequestWithAuth(post_request),
    body: JSON.stringify({ action: 'generate_report', ...params }, replacer)
  };
  return doAPICall(`${API_URL}/ai-assist/generate_report`, data, callback);
}

export async function updateReport(params: {match_id: string}, callback: (result: any, status: number) => void) {
  const data = {
    ...getRequestWithAuth(post_request),
    body: JSON.stringify({ action: 'update_report', ...params }, replacer)
  };
  return doAPICall(`${API_URL}/ai-assist/update_report`, data, callback);
}

export async function improveReport(params: {match_id: string, tone: string}, callback: (result: any, status: number) => void) {
  const data = {
    ...getRequestWithAuth(post_request),
    body: JSON.stringify({ action: 'improve_report', ...params }, replacer)
  };
  return doAPICall(`${API_URL}/ai-assist/improve_report`, data, callback);
}

export async function getPrompts(callback: (result: Prompt[], status: number) => void) {
  const data = getRequestWithAuth(get_request);
  return doAPICall(`${API_URL}/prompts/`, data, callback);
}

export async function updatePrompt(prompt: Prompt, callback: (result: Prompt, status: number) => void) {
  const data = {
    ...getRequestWithAuth(put_request),
    body: JSON.stringify({ prompt_text: prompt.prompt_text }, replacer)
  };
  return doAPICall(`${API_URL}/prompts/${prompt.action}`, data, callback);
}

export async function resetPrompts(callback: (result: void, status: number) => void) {
  const data = getRequestWithAuth(post_request);
  return doAPICall(`${API_URL}/prompts/reset`, data, callback);
}

// Image upload
export async function uploadImage(file: File, callback: (result: { url: string }, status: number) => void) {
  const formData = new FormData();
  formData.append('file', file);
  const data = {
    ...getRequestWithAuth(api_request),
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'Accept-Language': getCurrentLocale(),
      'X-Requested-With': 'XMLHttpRequest'
      // Authorization header will be added dynamically in doAPICall
    },
    body: formData
  };
  return doAPICall(`${API_URL}/upload-image/`, data, callback);
}

export async function getClubs(callback: (result: Club[], status: number) => void) {
  const data = getRequestWithAuth(get_request);
  return doAPICall(`${API_URL}/session/clubs`, data, callback);
}

export async function updateMatchHighlights(reportId: string, highlights: any[], callback: (result: any, status: number) => void) {
  const data = {
    ...getRequestWithAuth(post_request),
    body: JSON.stringify(highlights, replacer)
  };
  return doAPICall(`${API_URL}/reports/${reportId}/highlights`, data, callback);
}

// Get a single match report by ID
export async function getMatch(id: string, callback: (response: any, status: number) => void): Promise<void> {
  const data = getRequestWithAuth(get_request);
  return doAPICall(`${API_URL}/reports/${id}`, data, callback);
}

// Language settings
export async function updateLanguage(language: string, callback: (result: any, status: number) => void) {
  const data = {
    ...post_request,
    body: JSON.stringify({ language })
  };
  return doAPICall(`${API_URL}/session/language`, data, callback);
}

// Get available languages
export async function getLanguages(callback: (result: any, status: number) => void) {
  const data = getRequestWithAuth(get_request);
  return doAPICall(`${API_URL}/session/languages`, data, callback);
}


export async function switchTeam(teamId: string, callback: (result: any, status: number) => void) {
  const data = {
    ...getRequestWithAuth(post_request),
    body: JSON.stringify({ team_id: teamId }, replacer)
  };
  return doAPICall(`${API_URL}/account/switch-team`, data, callback);
}

// Check if user has seen onboarding popup (auto-marks as seen if not)
export async function checkOnboardingPopup(onboardingStep: string, callback: (result: { onboarding_step: string, has_seen: boolean, date_seen: string, newly_seen: boolean }, status: number) => void) {
  const data = getRequestWithAuth(get_request);
  return doAPICall(`${API_URL}/account/onboarding-popup/${encodeURIComponent(onboardingStep)}`, data, callback);
}


// Copy tasks from previous match
export async function copyTasksFromPreviousMatch(matchId: string, callback: (result: { task_list: any[], previous_match_id: string, previous_match_date: string }, status: number) => void) {
  const data = getRequestWithAuth(get_request);
  return doAPICall(`${API_URL}/reports/${matchId}/copy-tasks-from-previous`, data, callback);
}

// Create shareable link for match scoreboard
export async function createMatchShareLink(matchId: string, callback: (result: { token: string, expires_at: string }, status: number) => void) {
  const data = getRequestWithAuth(post_request);
  return doAPICall(`${API_URL}/reports/${matchId}/share`, data, callback);
}

// Get public scoreboard data (no authentication required)
export async function getPublicScoreboard(token: string, callback: (result: Match, status: number) => void) {
  const data = { ...get_request }; // No auth required for public endpoint
  return doAPICall(`${API_URL}/reports/public/${token}`, data, callback);
}

// Get public scoreboard updates for polling (no authentication required)
export async function getPublicScoreboardUpdates(token: string, callback: (result: { score_team: number, score_opponent: number, highlights: MatchHighlight[] }, status: number) => void) {
  const data = { ...get_request }; // No auth required for public endpoint
  return doAPICall(`${API_URL}/reports/public/${token}/updates`, data, callback);
}

// Analytics function - uses Umami client-side tracking
export async function trackEvent(action: string, eventData: Record<string, any> = {}, userId?: string): Promise<boolean> {
  try {
    // Check if umami is available (script loaded, only in production)
    if (typeof umami === 'undefined') {
      return false;
    }

    // Optionally identify the user for session tracking
    if (userId) {
      umami.identify(userId);
    }

    // Track the event with data
    umami.track(action, eventData);
    return true;

  } catch (error) {
    console.warn('Analytics tracking error:', error);
    return false;
  }
}